import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const LS_SID = "mp_demo_sid";

/**
 * Anonymous demo visitor id. Created on the first generation and kept in this
 * browser. It carries nothing about the person - its only job is to join
 * "generated a day in the demo" to "signed up later".
 *
 * Storage that refuses writes (private mode) yields an empty string: the demo
 * still works, only the join is lost, which undercounts the funnel rather than
 * breaking anything.
 */
export function demoSid() {
  try {
    const existing = localStorage.getItem(LS_SID);
    if (existing) return existing;
    const fresh =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(LS_SID, fresh);
    return fresh;
  } catch {
    return "";
  }
}

const LS_SRC = "mp_src";
const LS_SRC_META = "mp_src_meta";

// Referrer host -> the one word we file it under. Ordered, and the order is
// the point: gemini.google.com must be matched before the google rule, or
// every AI answer engine disappears into "search" - which for this product is
// the distinction most worth keeping, not a nuance.
//
// A host we do not recognise keeps its own name rather than being invented
// into a category. The raw host rides along in ref_host either way, so nothing
// is lost by admitting we have not classified it yet; when one starts showing
// up often it costs one line here and no migration.
const REF_RULES = [
  // Answer engines, before search.
  [/^chatgpt\.com$/, "chatgpt"],
  [/^chat\.openai\.com$/, "chatgpt"],
  [/^(www\.)?perplexity\.ai$/, "perplexity"],
  [/^gemini\.google\.com$/, "gemini"],
  [/^claude\.ai$/, "claude"],
  [/^copilot\.microsoft\.com$/, "copilot"],
  // Search.
  [/(^|\.)google\.[a-z.]{2,}$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)yandex\.[a-z.]{2,}$/, "yandex"],
  [/(^|\.)yahoo\.com$/, "yahoo"],
  // Social and community.
  [/(^|\.)reddit\.com$/, "reddit"],
  [/(^|\.)(facebook\.com|fb\.com)$/, "facebook"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/^(x\.com|twitter\.com|t\.co)$/, "x"],
  [/(^|\.)(linkedin\.com|lnkd\.in)$/, "linkedin"],
  [/(^|\.)(youtube\.com|youtu\.be)$/, "youtube"],
  [/^(t\.me|web\.telegram\.org|telegram\.org)$/, "telegram"],
  [/^(wa\.me|api\.whatsapp\.com)$/, "whatsapp"],
  [/(^|\.)pinterest\.[a-z.]{2,}$/, "pinterest"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/^news\.ycombinator\.com$/, "hn"],
];

// The query keys we read, and therefore the only ones we strip. Leaving a key
// we never looked at would be tidier to no purpose; taking out one another
// page still reads - token, session_id, mode - would break that page.
const CLAIMED_PARAMS = ["s", "utm_source", "utm_medium", "utm_campaign"];

/** trim + lowercase + cap. For a value that names a channel. */
function clipCode(raw, max) {
  return typeof raw === "string" ? raw.trim().toLowerCase().slice(0, max) : "";
}

/** trim + cap, case kept. For a path, where case is not ours to flatten. */
function clipRaw(raw, max) {
  return typeof raw === "string" ? raw.trim().slice(0, max) : "";
}

/**
 * The referring host, or "" when there is none we may count.
 *
 * Our own domain is not a source. Someone reading /faq and clicking through to
 * the landing page refers themselves, and counting that would turn internal
 * navigation into a channel - the loudest one there is, since every visitor
 * does it. The first test is against the host actually being served, so this
 * keeps working the day the domain changes; the rest cover the apex/www pair,
 * preview deployments and local development.
 */
function referrerHost() {
  try {
    const host = new URL(document.referrer).hostname.toLowerCase();
    const self = (window.location.hostname || "").toLowerCase();
    if (
      !host ||
      host === self ||
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "mealpill.app" ||
      host.endsWith(".mealpill.app") ||
      host.endsWith(".vercel.app")
    ) {
      return "";
    }
    return host;
  } catch {
    return "";   // no referrer at all, or one that will not parse
  }
}

/** Host -> channel word. An unknown host keeps its own name, minus "www.". */
function referrerName(host) {
  const bare = host.replace(/^www\./, "");
  for (const [re, name] of REF_RULES) {
    if (re.test(host) || re.test(bare)) return name;
  }
  return bare.slice(0, 32);
}

/**
 * What brought this visitor, decided once and kept.
 *
 * Four answers, first non-empty wins: the short code from ?s=, an utm_source
 * we attached ourselves, the referring host filed under one word, and failing
 * all three the explicit string "direct".
 *
 * "direct" is written out rather than left as an absent key on purpose. An
 * absent key cannot be told apart from "we were not asking yet", and a number
 * resting on that silence reads as fact. Same reasoning as the unreadable
 * dates beside the funnel: make the gap visible instead of letting it pass.
 *
 * First touch wins. Someone who arrives from a poster, leaves, and comes back
 * a week later through a search result is still credited to the poster: the
 * question is "what made them find us", not "what did they click last".
 *
 * A visitor whose sid predates this function gets "pre_tracking", never a
 * computed value: their first touch happened before we could record one, and
 * writing today's referrer onto a visit from July would invent a channel for
 * it. captureSource runs from index.js before React mounts and demoSid() is
 * only ever called from inside a component, so a sid present here can only
 * have come from an earlier visit - which is how we can tell without changing
 * demoSid to report it.
 *
 * The claimed parameters are stripped from the address afterwards so they do
 * not travel on into shares, bookmarks or the canonical URL.
 */
export function captureSource() {
  let computed = "direct";
  let meta = null;
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const host = referrerHost();
    computed =
      clipCode(params.get("s"), 32) ||
      clipCode(params.get("utm_source"), 32) ||
      (host ? referrerName(host) : "") ||
      "direct";
    // Kept beside the summary because a campaign cannot be reconstructed
    // later: an event that did not carry it never will. Caps mirror the
    // server's, so nothing is silently truncated on arrival.
    meta = {
      utm_medium: clipCode(params.get("utm_medium"), 32),
      utm_campaign: clipCode(params.get("utm_campaign"), 64),
      ref_host: host.slice(0, 64),
      landing_path: clipRaw(url.pathname, 128),
    };
    let touched = false;
    for (const key of CLAIMED_PARAMS) {
      if (params.has(key)) {
        params.delete(key);
        touched = true;
      }
    }
    if (touched) {
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  } catch {
    /* an address we cannot parse still gets an honest "direct" below */
  }

  try {
    const stored = localStorage.getItem(LS_SRC);
    if (stored) return stored;
    const value = readDemoSid() ? "pre_tracking" : computed;
    localStorage.setItem(LS_SRC, value);
    // No details for a pre_tracking visitor: we do not have theirs, and
    // today's would describe the wrong visit.
    if (value === computed && meta) {
      localStorage.setItem(LS_SRC_META, JSON.stringify(meta));
    }
    return value;
  } catch {
    return computed;   // storage refused; the answer is still the true one
  }
}

/** Read-only: what we decided earlier, without touching the address again. */
export function readSource() {
  try {
    return localStorage.getItem(LS_SRC) || "";
  } catch {
    return "";
  }
}

/**
 * The source fields for one funnel event, ready to spread into the body.
 *
 * Read from storage rather than from the current page, because the two can be
 * different visits: someone whose first touch was /faq reaches the landing
 * page later, by which point the referrer is our own and the marks are gone.
 * landing_path therefore means "where this visitor first arrived", not "where
 * this event was sent from" - the name reads the other way round, which is
 * exactly why it is written down here.
 *
 * Visitors captured before mp_src_meta existed have the summary and no
 * details. Their event travels without those keys; nothing is filled in on
 * their behalf.
 */
function sourcePayload() {
  // The fallback is unreachable in practice: with no storage there is no sid
  // either, and reportFunnelEvent gives up before it reaches this.
  const out = { source: readSource() || "direct" };
  try {
    const raw = localStorage.getItem(LS_SRC_META);
    if (!raw) return out;
    const meta = JSON.parse(raw);
    for (const key of ["utm_medium", "utm_campaign", "ref_host", "landing_path"]) {
      if (meta && meta[key]) out[key] = meta[key];
    }
  } catch {
    /* a meta value we cannot read is one we do not send */
  }
  return out;
}

/** Read-only: a signup that never touched the demo must not invent a demo. */
export function readDemoSid() {
  try {
    return localStorage.getItem(LS_SID) || "";
  } catch {
    return "";
  }
}

/**
 * One funnel step the browser knows about. Fire-and-forget on purpose: a
 * visitor must never wait on, or fail because of, our own measurement.
 *
 * The source rides on every event, attached here rather than by the caller. A
 * caller who forgets it does not lose a step - the step is the event type - it
 * loses that visitor's channel, silently and permanently. That is not a thing
 * to leave to whoever writes the next page.
 *
 * sourcePayload is spread LAST, deliberately. The source is canonical, and an
 * event's own field must not be able to overwrite it: not on purpose, and not
 * by someone one day passing a key of the same name in extra.
 *
 * keepalive matters here - every one of these fires immediately before a
 * navigation, and without it the browser is free to drop the request.
 */
export function reportFunnelEvent(eventType, extra = {}) {
  try {
    const sid = demoSid();
    if (!sid) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sid,
        event_type: eventType,
        ...extra,
        ...sourcePayload(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* measurement is never worth an exception in front of the visitor */
  }
}

/**
 * Once per page load, and not once per page. Moving between two marketing
 * addresses in the browser is still one arrival: React mounts a second page
 * component, but the visitor did not arrive twice, and a row saying they did
 * would be a row about our routing rather than about them.
 *
 * A module variable, deliberately. What clears it is a full document load - F5,
 * a new tab, a link followed in from outside - which is exactly the boundary
 * "an arrival" means. localStorage would outlive the visit and silence the next
 * one; a ref would need a component to survive in, and the whole point is that
 * no component owns this.
 */
let landingReported = false;

export function reportLandingOnce() {
  if (landingReported) return;
  landingReported = true;
  reportFunnelEvent("landing_view");
}

/**
 * Returns YYYY-MM-DD in LOCAL timezone (not UTC).
 * Use instead of new Date().toISOString().split("T")[0]
 * which returns UTC date and causes -1 day shift for UTC+ timezones.
 */
export function toLocalDateStr(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * "CA$3.80-5.40" — вилка вартості рецепта. Валюта підписана як CA$, так само
 * як у цінниках підписки (subPrice, subPriceYear в i18n).
 *
 * Межі — це p10/p90 цін магазинних SKU всередині підтипу, а не похибка: нижня
 * означає «всюди купили найдешевше», верхня — «всюди найдорожче». Тому дефіс,
 * а не ±. Коли межі збіглися (у підтипа одне спостереження), дефіс зайвий —
 * друге число нічого не додає, і рядок виглядав би як "CA$3.80-3.80".
 *
 * null повертається на будь-якому непридатному вході: викликач тоді просто не
 * рендерить рядок, замість того щоб показати "CA$NaN".
 */
export function formatPriceRange(min, max) {
  const lo = Number(min);
  const hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (lo.toFixed(2) === hi.toFixed(2)) return `CA$${lo.toFixed(2)}`;
  return `CA$${lo.toFixed(2)}-${hi.toFixed(2)}`;
}
